import { BreadcrumbItem } from "@/app/(site)/_components/Molecules/Breadcrumbs"
import { capitalizeWords } from "@/app/(site)/_utils/misc"
import { GlobalState, Size, Variant } from "@/app/(site)/contexts/ecommerce/types"
import { ColorSwatch } from "@/app/(site)/contexts/Global/types"
import { SHIPPING_COUNTRIES } from "@/data/shipping-countries"
import { SHIPPING_COUNTRY_CODES } from "@/data/shipping-country-code"
import { CENTRA_LANGUAGE_CODE } from "@/data/supported-languages"
import { CENTRA_MARKET_CODE } from "@/data/supported-markets"
import { CENTRA_PRICELIST_CODE } from "@/data/supported-pricelists"
import { RequiredTokenAndSelectionMetadata } from "@/types/cookie-types"
import { create } from "zustand"

interface GlobalActions {
    // QuickShop
    toggleQuickShopModal: () => void
    closeQuickShopModal: () => void

    // Cart
    toggleCartMenu: () => void
    closeCartMenu: () => void

    // Mobile menu
    setShowMobileMenu: (show: boolean) => void
    showMobileMenu: boolean

    // Cookie bar
    toggleCookieBar: () => void
    closeCookieBar: () => void

    // Market menu
    toggleMarketMenu: () => void
    closeMarketMenu: () => void

    // Nav menu
    toggleNavMenu: () => void
    setShowNavMenu: (show: boolean) => void
    closeNavMenu: () => void

    // Search menu
    toggleSearchMenu: () => void
    closeSearchMenu: () => void

    // Store message
    toggleStoreMessage: () => void
    closeStoreMessage: () => void

    // Size guide menu
    toggleSizeGuideMenu: () => void
    toggleLensGuideModal: () => void

    // Lens Guide menu
    closeLensGuideModal: () => void
    closeSizeGuideMenu: () => void

    // Sizes
    setSizes: (sizes: Size[]) => void
    setSelectedSizeName: (setSelectedSizeName: string) => void

    // Color Swatches
    setColorSwatches: (colorSwatches: ColorSwatch[]) => void
    setSelectedVariant: (selectedVariant: Variant) => void

    // Variant category name
    setVariantCategoryName: (variantCategoryName: BreadcrumbItem[]) => void

    // Notify Me modal
    toggleNotifyModal: (isOpen: boolean) => void

    // Search Modal
    toggleSearchModal: (isOpen: boolean) => void

    // Country picker
    setShowCountryPicker: (show: boolean) => void

    // Centra Session
    setCentraSession: (centraSession: RequiredTokenAndSelectionMetadata) => void

    // Filter Modal
    toggleFilterModal: (isOpen: boolean) => void

    // Wrong site modal
    toggleWrongSiteModal: (isOpen: boolean) => void
}

const useEcommerceStore = create<GlobalState & GlobalActions>(set => ({
    // Initial states
    isQuickShopModalOpen: false,
    isLoadingImages: false,
    selectedQuickShopProduct: null,
    isCartMenuOpen: false,
    isCookieBarOpen: false,
    isMarketMenuOpen: false,
    isMobileFilterOpen: false,
    isWrongSiteModalOpen: false,
    isNavMenuOpen: false,
    isSearchMenuOpen: false,
    isStoreMessageOpen: false,
    isNotifyMeModalOpen: false,
    isSearchModalOpen: false,
    isLensGuideModalOpen: false,
    isSizeGuideMenuOpen: false,
    showCountryPicker: false,
    sizes: [],
    selectedSizeName: "",
    colorSwatches: [],
    selectedVariant: {} as Variant,
    variantCategoryName: [],
    showMobileMenu: false,
    centraSession: {
        token: "",
        metadata: {
            market: CENTRA_MARKET_CODE.Eu,
            language: CENTRA_LANGUAGE_CODE.En,
            currency: CENTRA_PRICELIST_CODE.eur.toLocaleUpperCase(),
            country: {
                code: SHIPPING_COUNTRY_CODES.Germany.toLocaleUpperCase(),
                name: capitalizeWords(SHIPPING_COUNTRIES.Germany)
            },
            currencyFormat: {
                name: "",
                prefix: "",
                suffix: "",
                decimalPoint: "",
                thousandsSeparator: "",
                decimalDigits: "",
                uri: ""
            }
        }
    },

    toggleQuickShopModal: () =>
        set(state => ({ isQuickShopModalOpen: !state.isQuickShopModalOpen })),
    closeQuickShopModal: () => set({ isQuickShopModalOpen: false }),

    toggleNavMenu: () => set(state => ({ isNavMenuOpen: !state.isNavMenuOpen })),
    setShowNavMenu: (show: boolean) => set({ isNavMenuOpen: show }),

    // Actions
    toggleCartMenu: () => set(state => ({ isCartMenuOpen: !state.isCartMenuOpen })),
    closeCartMenu: () => set({ isCartMenuOpen: false }),

    // Cookie bar
    toggleCookieBar: () => set(state => ({ isCookieBarOpen: !state.isCookieBarOpen })),
    closeCookieBar: () => set({ isCookieBarOpen: false }),

    // Market menu
    toggleMarketMenu: () => set(state => ({ isMarketMenuOpen: !state.isMarketMenuOpen })),
    closeMarketMenu: () => set({ isMarketMenuOpen: false }),

    // Nav menu
    closeNavMenu: () => set({ isNavMenuOpen: false }),

    // Mobile menu
    setShowMobileMenu: (show: boolean) => set({ showMobileMenu: show }),

    // Search menu
    toggleSearchMenu: () => set(state => ({ isSearchMenuOpen: !state.isSearchMenuOpen })),
    closeSearchMenu: () => set({ isSearchMenuOpen: false }),

    // Lens Guide menu
    toggleLensGuideModal: () =>
        set(state => ({ isLensGuideModalOpen: !state.isLensGuideModalOpen })),
    closeLensGuideModal: () => set({ isLensGuideModalOpen: false }),

    // Store message
    toggleStoreMessage: () =>
        set(state => ({ isStoreMessageOpen: !state.isStoreMessageOpen })),
    closeStoreMessage: () => set({ isStoreMessageOpen: false }),

    // Size guide menu
    toggleSizeGuideMenu: () =>
        set(state => ({ isSizeGuideMenuOpen: !state.isSizeGuideMenuOpen })),
    closeSizeGuideMenu: () => set({ isSizeGuideMenuOpen: false }),

    // Country picker
    setShowCountryPicker: show => set({ showCountryPicker: show }),

    // Sizes and color swatches
    setSizes: sizes => set({ sizes }),
    setColorSwatches: colorSwatches => set({ colorSwatches }),
    setSelectedSizeName: selectedSizeName => set({ selectedSizeName }),

    setVariantCategoryName: variantCategoryName => set({ variantCategoryName }),

    setSelectedVariant: selectedVariant => set({ selectedVariant }),
    toggleNotifyModal: isOpen => set({ isNotifyMeModalOpen: isOpen }),

    toggleSearchModal: isOpen => set({ isSearchModalOpen: isOpen }),

    // Centra Session
    setCentraSession: centraSession => set({ centraSession }),

    // Filter Modal
    toggleFilterModal: isOpen => set({ isMobileFilterOpen: isOpen }),

    // Wrong site modal
    toggleWrongSiteModal: isOpen => set({ isWrongSiteModalOpen: isOpen })
}))

export default useEcommerceStore
